import { useField, useFormikContext } from "formik";
import {
  DatePickerField,
  DisplayFormErrors,
  DrawerFooterContent,
  InputField,
  SelectField,
  SwitchField,
  useCultures,
  VerticalForm,
} from "melco-shared-logic";
import { Button, Row, Title } from "melco-ui";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { DisplayProductsDropDown } from "../../dropdown/DisplayProductsDropDown";
import { useState } from "react";
import { DisplayLevelSubscriptionDropDown } from "../../dropdown/DisplayLevelSubscriptionDropDown";
import { DisplayBlankDropDown } from "../../dropdown/DisplayBlankDropDown";
import { VIRTUAL_SECURITY_TYPE } from "../../../helper/licenseHelper";
import { useRolesContext } from "../../../hooks/roles/useRolesContext";
import { DisplayProductVersionsDropDown } from "../../dropdown/DisplayProductVersionsDropDown";
import { useAllProducts } from "../../../hooks/useAllProduts";
import { CreateLicenseFormValueType } from "./CreateLicenseForm";
import { DropDownValueLabelObj } from "../../../helper/commonHelper";
import dayjs from "dayjs";
import { DisplayRefferalSalesPersonsDropDown } from "../../dropdown/DisplayRefferalsDropDown";

const AlertWrapper = styled.div`
  margin-top: 25px;
`;

type DisplayCreateLicenseFormProps = {
  licenseFormValues?: CreateLicenseFormValueType;
  licenseDisplayValues?: any;
  callback: (v: any) => any;
};

export const DisplayCreateLicenseForm: React.FC<
  DisplayCreateLicenseFormProps
> = ({ licenseDisplayValues, licenseFormValues, callback }) => {
  const { t } = useTranslation();
  const { submitForm, setFieldValue } = useFormikContext();
  const { cultures } = useCultures();

  const [selectedProductValue, setSelectedProductValue] = useState(
    licenseFormValues?.product_code ?? ""
  );
  const [selectedVersionValue, setSelectedVersionValue] = useState(
    licenseFormValues?.version_number ?? ""
  );
  const [productIsSubscription, setProductIsSubscription] = useState(
    licenseDisplayValues?.productIsSubscription ?? false
  );
  const [createConfirmDetailsToDisplay, setCreateConfirmDetailsToDisplay] =
    useState(licenseDisplayValues);

  const [, , versionIdHelpers] = useField("version_id");
  const [, , subscriptionLevelHelpers] = useField("subscription_code");
  const allProducts = useAllProducts();
  const { roles } = useRolesContext();

  let createAccessFilterListArray =
    roles?.VIRTUAL_SECURITY_GENERATE_ACCESS_LIST_FILTER_LIST?.toString()?.split(
      ","
    ) ?? [];
  createAccessFilterListArray.map((value) => value.trim());


  const products = allProducts?.products.filter((p) => {
    if (
      roles.VIRTUAL_SECURITY_GENERATE_ACCESS_LIST &&
      roles.VIRTUAL_SECURITY_GENERATE_ACCESS_LIST_FILTER_LIST &&
      roles.VIRTUAL_SECURITY_GENERATE_ACCESS_LIST_FILTER_LIST !== ""
    ) {
      if (p.code) {
        return (
          p.security_type === VIRTUAL_SECURITY_TYPE &&
          createAccessFilterListArray.includes(p.code)
        );
      }
      else { return false }
    } else {
      return p.security_type === VIRTUAL_SECURITY_TYPE;
    }
  });


  const resetProductRelatedDropDowns = async () => {
    setSelectedProductValue("");
    setSelectedVersionValue("");
    setCreateConfirmDetailsToDisplay({
      ...createConfirmDetailsToDisplay,
      product_code_label: undefined,
      version_number_label: undefined,
      subscription_code_label: undefined,
    });
  };

  const resetVersionRelatedDropDowns = async () => {
    setSelectedVersionValue("");
    setCreateConfirmDetailsToDisplay({
      ...createConfirmDetailsToDisplay,

      version_number_label: undefined,
      subscription_code_label: undefined,
    });
  };

  const callBackForSubscription = (v: any, subscription: any) => {
    setCreateConfirmDetailsToDisplay({
      ...createConfirmDetailsToDisplay,
      subscription_code_label: subscription.label,
    });
  };

  const callBackForProduct = async (v: any, product: any) => {
    await resetProductRelatedDropDowns();
    setSelectedProductValue(v);
    //Form.setFieldValue("abc", undefined);
    setCreateConfirmDetailsToDisplay({
      ...createConfirmDetailsToDisplay,
      product_code_label: product.label,
      productIsSubscription: product?.product?.is_subscription ?? false,
    });
    versionIdHelpers.setValue("");
    subscriptionLevelHelpers.setValue("");
    setProductIsSubscription(product.product.is_subscription);
    setFieldValue("subscription_start_date", null);
  };

  const callBackForVersion = async (v: any) => {
    /**
     * We have to use dropdown label as version value
     * because the api products/subscriptions takes version_number_list as a version number
     * This is version object  
     * {
            "id": "PVR-1",
            "version_number": "12",
            "created_date": "2023-11-07T19:51:55.04Z"
        }

        While Create new License we have to use id like PVR-1
        While we have to search for subscriptions (Level)  we have to use 12

        
          */

    await resetVersionRelatedDropDowns();
    setSelectedVersionValue(v.label);
    setCreateConfirmDetailsToDisplay({
      ...createConfirmDetailsToDisplay,
      version_number_label: v.label,
    });
    subscriptionLevelHelpers.setValue("");
  };

  // const callBackForRefferal = (v: string, refferal: any) => {
  //   setCreateConfirmDetailsToDisplay({ ...createConfirmDetailsToDisplay, "referral_code_label": refferal.label })
  // }


  const onClickNext = () => {
    callback(createConfirmDetailsToDisplay);
    submitForm();
  };

  const callBackForLanguage = (v: any, obj: any) => {
    setCreateConfirmDetailsToDisplay({
      ...createConfirmDetailsToDisplay,
      language: obj.label,
    });
  };

  return (
    <>
      <div>
        <Title level={4}>{t("licensemanager.create.title")}</Title>
        <VerticalForm>
          <AlertWrapper>
            <DisplayFormErrors />
          </AlertWrapper>

          <InputField
            name="melco_customer_id"
            label={t("licensemanager.create.form.melco_customer_id")}
          />
          <InputField
            name="email_address"
            label={t("licensemanager.create.form.email")}
          />
          <DisplayProductsDropDown
            defaultProducts={{
              list: products,
              total: allProducts.products.length,
            }}
            callbackFn={callBackForProduct}
          />
          {selectedProductValue ? (
            <DisplayProductVersionsDropDown
              selectedProductValue={selectedProductValue}
              callbackFn={callBackForVersion}
            />
          ) : (
            <DisplayBlankDropDown
              name="version_id"
              label={t("licensemanager.create.form.version")}
            />
          )}
          {selectedProductValue && selectedVersionValue ? (
            <DisplayLevelSubscriptionDropDown
              selectedProductValue={selectedProductValue}
              selectedVersionValue={selectedVersionValue}
              callbackFn={callBackForSubscription}
            />
          ) : (
            <DisplayBlankDropDown
              name="subscription_code"
              label={t("licenses.basic.search.level.title")}
            />
          )}
          {productIsSubscription ? (
            <DatePickerField
              label={t("licensemanager.create.form.subscription_start_date")}
              info={t(
                "licensemanager.create.form.subscription_start_date_info"
              )}
              name="subscription_start_date"
              disabledDate={(current) =>
                !!current && current.isBefore(dayjs().startOf("day"))
              }
              onChange={(date, dateString) => {
                setFieldValue("subscription_start_date", date ? date : null);
                setCreateConfirmDetailsToDisplay({
                  ...createConfirmDetailsToDisplay,
                  subscription_start_date: dateString,
                });
              }}
            />
          ) : null}

          {productIsSubscription ? (<DisplayRefferalSalesPersonsDropDown

            callbackFn={() => { }}
          />) : null}

          <SelectField
            name="language"
            label={t("licensemanager.create.form.language")}
            options={cultures.map((culture) => {
              return {
                label: culture.name,
                value: culture.code,
              };
            })}
            showSearch
            optionFilterProp="label"
            info={t("licensemanager.create.form.languageinfo")}
            onChange={(value: string, obj: DropDownValueLabelObj) => {
              callBackForLanguage(value, obj);
            }}
          />
          <SwitchField
            name="assign_license"
            label={t("licensemanager.create.form.dont_assign_license")}
          />
          <DrawerFooterContent>
            <Row justify="end">
              <Button
                style={{ minWidth: "80px" }}
                key="add-user-button"
                type="primary"
                onClick={onClickNext}
              >
                {t("licensemanager.buttons.next")}
              </Button>
            </Row>
          </DrawerFooterContent>
        </VerticalForm>
      </div>
    </>
  );
};
